import axios from 'axios';

import { ref } from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import { useConfirm } from 'primevue/useconfirm';

const approvingCreativeId = ref<number | null>(null);

const cloningCreativeId = ref<number | null>(null);

const deletingCreativeId = ref<number | null>(null);

const restoringCreativeId = ref<number | null>(null);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export default () => {
  const i18n = useI18n();

  const toast = useToast();

  const confirm = useConfirm();

  const changeApprovalStatus = async (id: number, approvalStatus: number, onSuccess: (newStatus: number) => void): Promise<void> => {
    approvingCreativeId.value = id;

    try {
      await axios.post('/api/creative/changeApprovalStatusById', { id, approval_status: approvalStatus });
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: i18n.t('error'),
        detail: i18n.t('unknown_error'),
        life: 2000,
      });

      approvingCreativeId.value = null;

      return;
    }

    approvingCreativeId.value = null;

    onSuccess(approvalStatus);
  };

  const cloneCreative = async (id: number, onSuccess: (newId: number) => Promise<void> | void): Promise<void> => {
    cloningCreativeId.value = id;

    let newId: number;

    try {
      const response = await axios.post('/api/creative/cloneCreative', { id });

      newId = response.data.id;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: i18n.t('error'),
        detail: i18n.t('unknown_error'),
        life: 2000,
      });

      cloningCreativeId.value = null;

      return;
    }

    cloningCreativeId.value = null;

    await onSuccess(newId);
  };

  const confirmCloneCreative = (event: PointerEvent, id: number, onSuccess: (newId: number) => Promise<void> | void): void => {
    confirm.require({
      target: event.currentTarget as HTMLElement,
      message: 'Are you sure you want to clone this creative?',
      icon: 'pi pi-question-circle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Clone',
      rejectLabel: 'Cancel',
      rejectClass: 'p-button-outlined p-button-sm',
      acceptClass: 'p-button-sm',
      accept: () => {
        cloneCreative(id, onSuccess);
      },
    });
  };

  const deleteCreative = async (id: number, onSuccess: () => Promise<void> | void): Promise<void> => {
    deletingCreativeId.value = id;

    try {
      await axios.post('/api/creative/deleteCreativeById', { id });
    } catch {
      toast.add({
        severity: 'error',
        summary: i18n.t('error'),
        detail: i18n.t('unknown_error'),
        life: 2000,
      });

      deletingCreativeId.value = null;

      return;
    }

    deletingCreativeId.value = null;

    await onSuccess();
  };

  const confirmDeleteCreative = (event: PointerEvent, id: number, onSuccess: () => Promise<void> | void): void => {
    confirm.require({
      target: event.currentTarget as HTMLElement,
      message: 'Are you sure you want to delete this creative? It will also be removed from all campaigns it is used in. ⚠️',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      rejectClass: 'p-button-outlined p-button-sm',
      acceptClass: 'p-button-danger p-button-sm',
      accept: () => {
        deleteCreative(id, onSuccess);
      },
    });
  };

  const restoreCreative = async (id: number, onSuccess: () => Promise<void> | void): Promise<void> => {
    restoringCreativeId.value = id;

    try {
      await axios.post('/api/creative/restoreCreativeById', { id });
    } catch {
      toast.add({
        severity: 'error',
        summary: i18n.t('error'),
        detail: i18n.t('unknown_error'),
        life: 2000,
      });

      restoringCreativeId.value = null;

      return;
    }

    restoringCreativeId.value = null;

    await onSuccess();
  };

  return {
    approvingCreativeId,
    changeApprovalStatus,
    cloningCreativeId,
    cloneCreative,
    confirmCloneCreative,
    deletingCreativeId,
    deleteCreative,
    confirmDeleteCreative,
    restoringCreativeId,
    restoreCreative,
  };
};
